import type {
	ResolvedTheme,
	Theme,
} from "../components/global/theme/theme-provider"

/**
 * Type guard to check if a value is a valid theme
 */
export function isValidTheme(value: string | null): value is Theme {
	return value === "dark" || value === "light" || value === "system"
}

/**
 * Gets the system's preferred color scheme
 */
export function getSystemTheme(): ResolvedTheme {
	if (typeof window === "undefined") return "light"
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light"
}

/**
 * Gets a validated theme value from localStorage or returns fallback
 */
export function getStoredTheme(storageKey: string, fallback: Theme): Theme {
	if (typeof window === "undefined") return fallback
	try {
		const stored = window.localStorage.getItem(storageKey)
		return isValidTheme(stored) ? stored : fallback
	} catch {
		return fallback
	}
}

/**
 * Generates a script that applies the theme before React hydration to prevent flash.
 * This should be used in the document head to ensure immediate theme application.
 */
export function createThemeScript(storageKey = "cardnexus-admin-theme") {
	return `
		(function() {
			try {
				var storageKey = '${storageKey}';
				var theme = localStorage.getItem(storageKey) || 'system';
				var root = document.documentElement;
				var prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
				var systemTheme = prefersDark ? 'dark' : 'light';
				var target = theme === 'system' ? systemTheme : theme;
				
				// Validate theme value
				if (target !== 'dark' && target !== 'light') {
					target = 'light';
				}
				
				if (!root.classList.contains(target)) {
					root.classList.remove('light','dark');
					root.classList.add(target);
				}
			} catch (e) {
				// Fallback to light theme on error
				document.documentElement.classList.add('light');
			}
		})();
	`.trim()
}
